export const CLEANING_TEMPLATE_KEYS = [
  "dayOfReminderTemplate",
  "scheduleStartTemplate",
  "newMemberIntroTemplate",
  "followUpTemplate",
  "advanceReminderTemplate",
] as const;

export type CleaningTemplateKey = typeof CLEANING_TEMPLATE_KEYS[number];

export type CleaningNotificationRules = {
  reminderHour?: number;
  followUpDelayHours?: number;
  dayOfReminderTemplate?: string;
  scheduleStartTemplate?: string;
  newMemberIntroTemplate?: string;
  followUpTemplate?: string;
  advanceReminderTemplate?: string;
  // Delivery state is server-owned and intentionally not editable in settings.
  scheduleStartSentAt?: string;
  scheduleStartPending?: boolean;
  scheduleStartMemberIds?: Record<string, string>;
  introRosterInitializedAt?: string;
  introducedMemberIds?: Record<string, string>;
  launchLifecycle?: { anchorDate: string | null; initializedAt: string; suppressedReason: string | null };
};

export const DEFAULT_CLEANING_NOTIFICATION_RULES: Required<
  Pick<CleaningNotificationRules, "reminderHour" | "followUpDelayHours" | CleaningTemplateKey>
> = {
  reminderHour: 8,
  followUpDelayHours: 24,
  dayOfReminderTemplate: "Hi {{memberFirstName}}, {{assignmentDescription}} at {{propertyLabel}}. Submit your proof here: {{formLink}}",
  scheduleStartTemplate: "Hi {{memberFirstName}}, the cleaning program at {{propertyLabel}} starts {{programStart}}. {{assignmentDescription}}. This is an informational announcement; proof is requested on your assigned day.",
  newMemberIntroTemplate: "Welcome, {{memberFirstName}}! You're on the cleaning roster at {{propertyLabel}}. {{assignmentDescription}}. Proof is requested on your assigned day.",
  followUpTemplate: "Hi {{memberFirstName}}, we still need your cleaning proof for {{propertyLabel}}. {{assignmentDescription}}. Please submit it here: {{formLink}}",
  advanceReminderTemplate: "Hi {{memberFirstName}}, the cleaning program at {{propertyLabel}} starts {{programStart}}. {{assignmentDescription}}. Proof is requested on your assigned day.",
};

export function withCleaningNotificationDefaults(
  rules: CleaningNotificationRules | null | undefined,
): CleaningNotificationRules & typeof DEFAULT_CLEANING_NOTIFICATION_RULES {
  return { ...DEFAULT_CLEANING_NOTIFICATION_RULES, ...repairCleaningNotificationRules(rules).rules };
}

export function renderCleaningTemplate(template: string, variables: Record<string, string>): string {
  return template.replace(
    /\{\{(memberFirstName|memberName|propertyLabel|cleaningDay|formLink|period|programStart|weekStart|weekEnd|assignmentDescription)\}\}/g,
    (_match, key) => variables[key] ?? "",
  );
}

export function unsafeCleaningInformationalTemplate(template: string): boolean {
  return /\{\{\s*formLink\s*\}\}|\/service-form\/|(?:clean|complete|submit|proof).{0,35}\bearly\b|\bearly\b.{0,35}(?:clean|complete|submit|proof)/i.test(template);
}

const legacyTemplates: Partial<Record<CleaningTemplateKey, string>> = {
  followUpTemplate: "Hi {{memberFirstName}}, we still need your cleaning proof for {{propertyLabel}} ({{cleaningDay}}). Please submit it here: {{formLink}}",
  dayOfReminderTemplate: "Hi {{memberFirstName}}, it's your cleaning {{period}} at {{propertyLabel}}. Submit your proof here: {{formLink}}",
  scheduleStartTemplate: "Hi {{memberFirstName}}, the cleaning schedule has started at {{propertyLabel}}. Your assigned {{period}} is {{cleaningDay}}.",
  newMemberIntroTemplate: "Welcome, {{memberFirstName}}! You're on the cleaning roster at {{propertyLabel}}. Your assigned {{period}} is {{cleaningDay}}.",
  advanceReminderTemplate: "Hi {{memberFirstName}}, your cleaning {{period}} at {{propertyLabel}} is coming up on {{cleaningDay}}. You can complete it early and submit proof here: {{formLink}}",
};
export function repairCleaningNotificationRules(input: CleaningNotificationRules | null | undefined) {
  const rules = { ...(input ?? {}) };
  let changed = false;
  for (const key of CLEANING_TEMPLATE_KEYS) {
    if (legacyTemplates[key] && rules[key] === legacyTemplates[key]) {
      rules[key] = DEFAULT_CLEANING_NOTIFICATION_RULES[key]; changed = true;
    }
  }
  return { rules, changed };
}

export function cleaningTemplateVariables(member: {name?: string | null}, assignment: {scheduledDate: string; period: string}, propertyLabel: string, programStart: string, formLink = "") {
  const day = String(assignment.scheduledDate).slice(0, 10);
  const end = new Date(Date.parse(`${day}T00:00:00Z`) + 6 * 86400000).toISOString().slice(0, 10);
  const name = member.name || "Assigned member";
  return { memberFirstName: name.split(" ")[0], memberName: name, propertyLabel,
    cleaningDay: day, programStart, formLink, period: assignment.period,
    weekStart: day, weekEnd: end, assignmentDescription: assignment.period === "week"
      ? `${name} is assigned the cleaning week ${day} through ${end}`
      : `${name} is assigned cleaning on ${day}` };
}